import type { TranslationKey } from '@heidi/runtime/i18n';
import { useIsNonAdminEditingOrganizationTemplate } from './useIsNonAdminEditingOrganizationTemplate';
import { useIsNonAdminEditingTeamTemplate } from './useIsNonAdminEditingTeamTemplate';
import { useIsTeamAdminEditingOtherTeamsTemplate } from './useIsTeamAdminEditingOtherTeamsTemplate';

export enum TemplateEditorDisableReason {
  None = 'None',
  NonAdminEditingTeamTemplate = 'NonAdminEditingTeamTemplate',
  TeamAdminEditingOtherTeamsTemplate = 'TeamAdminEditingOtherTeamsTemplate',
  NonAdminEditingOrganizationTemplate = 'NonAdminEditingOrganizationTemplate',
  CannotEditHeidiTemplate = 'CannotEditHeidiTemplate',
}

/**
 * Converts a TemplateEditorDisableReason enum value to its corresponding translation key.
 * Returns null for None since there's no translation needed when the editor is not disabled.
 */
export const getTemplateEditorDisableReasonTranslationKey = (
  reason: TemplateEditorDisableReason
): TranslationKey | null => {
  const reasonToTranslationKeyMap: Record<
    TemplateEditorDisableReason,
    TranslationKey | null
  > = {
    [TemplateEditorDisableReason.None]: null,
    [TemplateEditorDisableReason.NonAdminEditingTeamTemplate]:
      'templateEditor.disableReason.nonAdminEditingTeamTemplate' as TranslationKey,
    [TemplateEditorDisableReason.TeamAdminEditingOtherTeamsTemplate]:
      'templateEditor.disableReason.teamAdminEditingOtherTeamsTemplate' as TranslationKey,
    [TemplateEditorDisableReason.NonAdminEditingOrganizationTemplate]:
      'templateEditor.disableReason.nonAdminEditingOrganizationTemplate' as TranslationKey,
    [TemplateEditorDisableReason.CannotEditHeidiTemplate]:
      'templateEditor.disableReason.cannotEditHeidiTemplate' as TranslationKey,
  };

  return reasonToTranslationKeyMap[reason];
};

export const useUserTemplatePermissions = () => {
  // When a non-admin or team admin (editing other team's template) tries to edit,
  // the clone workflow is triggered automatically.
  // Doc: https://www.notion.so/heidihealth/Template-Editor-Team-Organization-related-features-2b0ca630286e80ab8473ed91bf669de0
  const { isNonAdminEditingTeamTemplate } = useIsNonAdminEditingTeamTemplate();
  const { isTeamAdminEditingOtherTeamsTemplate } =
    useIsTeamAdminEditingOtherTeamsTemplate();
  const { isNonAdminEditingOrganizationTemplate } =
    useIsNonAdminEditingOrganizationTemplate();

  const shouldDisableTemplateEditor =
    isNonAdminEditingTeamTemplate ||
    isTeamAdminEditingOtherTeamsTemplate ||
    isNonAdminEditingOrganizationTemplate;

  let reason: TemplateEditorDisableReason;
  if (isNonAdminEditingTeamTemplate) {
    reason = TemplateEditorDisableReason.NonAdminEditingTeamTemplate;
  } else if (isTeamAdminEditingOtherTeamsTemplate) {
    reason = TemplateEditorDisableReason.TeamAdminEditingOtherTeamsTemplate;
  } else if (isNonAdminEditingOrganizationTemplate) {
    reason = TemplateEditorDisableReason.NonAdminEditingOrganizationTemplate;
  } else {
    reason = TemplateEditorDisableReason.None;
  }

  const reasonTranslationKey = getTemplateEditorDisableReasonTranslationKey(reason);

  return {
    shouldDisableTemplateEditor,
    reason,
    reasonTranslationKey,
  };
};
